/**
 * Inbox and texting contracts (BUILD-PLAN 2.4 "Communications", M11, 5.3).
 *
 * Wave-1 interface: `Interaction` (the one timeline table), `Thread`, plus the
 * inbound normalisation, send request, consent ledger and keyword rules the
 * consent engine and Y-confirm flow share.
 *
 * Engagement data is always Velora's (BUILD-PLAN 2.5.5): nothing here is
 * written into PulseSync mirror tables. In S0 a manual text is mirrored to
 * the PMS communication log through the `commlog.add` command only.
 *
 * Compliance sources (confirm current scope with counsel before launch):
 * - CTIA Messaging Principles and Best Practices (May 2023): opt-out
 *   keywords, one confirmation message, HELP response.
 * - FCC 24-24, Report and Order on revocation of consent (2024), 47 CFR
 *   64.1200(a)(10)-(12): STOP, QUIT, END, REVOKE, OPT OUT, CANCEL and
 *   UNSUBSCRIBE as a sole reply are per se revocations; revocation by any
 *   reasonable means; one confirmation text within 5 minutes.
 * - FCC 15-72, 2015 TCPA Omnibus Declaratory Ruling, paragraphs 146-147:
 *   healthcare exemption limits (one message per day, three per week,
 *   no billing or marketing content, opt-out in every message).
 * - 47 CFR 64.1200(c)(1): no telephone solicitation before 8 a.m. or after
 *   9 p.m. at the called party's location. Velora applies the window to all
 *   non-urgent automated sends (texting-inbox 4.7).
 */
import { z } from 'zod';
import { E164Schema, SmsPurposeSchema } from './patients.js';
import {
  CursorSchema,
  IdempotencyKeySchema,
  IdSchema,
  InstantSchema,
  LocationOwnedSchema,
  RowMetaSchema,
  WriteBackStateSchema,
} from './sync.js';

/* ------------------------------------------------------------------------ */
/* Vocabularies                                                              */
/* ------------------------------------------------------------------------ */

export const INTERACTION_CHANNELS = [
  'sms',
  'mms',
  'call',
  'voicemail',
  'email',
  'chat',
  'ai_action',
  'form',
  'payment',
  'review',
  'note',
] as const;
export const InteractionChannelSchema = z.enum(INTERACTION_CHANNELS);
export type InteractionChannel = z.infer<typeof InteractionChannelSchema>;

export const DirectionSchema = z.enum(['inbound', 'outbound', 'internal']);
export type Direction = z.infer<typeof DirectionSchema>;

export const InteractionActorKindSchema = z.enum(['user', 'ai', 'automation', 'patient']);
export type InteractionActorKind = z.infer<typeof InteractionActorKindSchema>;

/**
 * Delivery and lifecycle status. Outbound SMS follows the carrier callback
 * (`queued -> sent -> delivered | undelivered | failed`); `held_*` statuses
 * are Velora guards that delay a send instead of dropping it.
 */
export const INTERACTION_STATUSES = [
  'draft',
  'scheduled',
  'held_quiet_hours',
  'held_frequency_cap',
  'held_awaiting_registration',
  'queued',
  'sent',
  'delivered',
  'undelivered',
  'failed',
  'received',
  'read',
  'blocked_opted_out',
] as const;
export const InteractionStatusSchema = z.enum(INTERACTION_STATUSES);
export type InteractionStatus = z.infer<typeof InteractionStatusSchema>;

/** Plain-English failure classes (texting-inbox 4.8). */
export const DeliveryFailureSchema = z.enum([
  'landline',
  'unreachable',
  'carrier_filtered',
  'opted_out',
  'rate_limited',
  'unknown',
]);
export type DeliveryFailure = z.infer<typeof DeliveryFailureSchema>;

export const INTERACTION_LINK_KINDS = [
  'appointment',
  'patient',
  'claim',
  'procedure',
  'lead',
  'payment',
  'form_submission',
  'review_request',
  'call',
  'task',
] as const;
export const InteractionLinkSchema = z.object({
  kind: z.enum(INTERACTION_LINK_KINDS),
  id: IdSchema,
});
export type InteractionLink = z.infer<typeof InteractionLinkSchema>;

/** One MMS or email attachment. Bytes live in object storage (presigned, at most 5 min). */
export const AttachmentSchema = z.object({
  id: IdSchema,
  contentType: z.string().regex(/^[a-z]+\/[a-z0-9.+-]+$/i),
  sizeBytes: z.number().int().nonnegative(),
  fileName: z.string().max(200).nullable(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

/**
 * GSM-7 or UCS-2 encoding (3GPP TS 23.038) and segment count. A 1600-character
 * send (the provider body limit) is 11 GSM-7 segments of 153 or 24 UCS-2
 * segments of 67; inbound bodies can be longer. Concatenated SMS uses an
 * 8-bit part count, so 255 is the protocol maximum (3GPP TS 23.040 9.2.3.24.1).
 */
export const SMS_MAX_SEGMENTS = 255;
export const SmsMetaSchema = z.object({
  encoding: z.enum(['gsm7', 'ucs2']),
  segments: z.number().int().min(1).max(SMS_MAX_SEGMENTS),
  /** Carrier or provider error code, for support. */
  errorCode: z.string().max(20).nullable(),
  failure: DeliveryFailureSchema.nullable(),
});
export type SmsMeta = z.infer<typeof SmsMetaSchema>;

/* ------------------------------------------------------------------------ */
/* Interaction (the one timeline row)                                        */
/* ------------------------------------------------------------------------ */

export const InteractionSchema = z
  .object({
    id: IdSchema,
    ...LocationOwnedSchema.shape,
    threadId: IdSchema.nullable(),
    contactId: IdSchema.nullable(),
    patientId: IdSchema.nullable(),
    channel: InteractionChannelSchema,
    direction: DirectionSchema,
    /** Office line (DID) in E.164 for SMS and calls. */
    lineId: z.string().max(64).nullable(),
    actorKind: InteractionActorKindSchema,
    actorUserId: IdSchema.nullable(),
    /** Denormalised for the bubble ("Ana, 9:14"). */
    actorName: z.string().max(200).nullable(),
    status: InteractionStatusSchema.nullable(),
    /** Message text. PHI: never logged, never in push payloads. Null once removed. */
    body: z.string().max(10_000).nullable(),
    /** Object key for long bodies, transcripts or email MIME. */
    bodyRef: z.string().max(500).nullable(),
    attachments: z.array(AttachmentSchema),
    callId: IdSchema.nullable(),
    links: z.array(InteractionLinkSchema),
    /** Consent category of an outbound message. */
    purpose: SmsPurposeSchema.nullable(),
    /**
     * Provider message id (Twilio MessageSid, Stratus message id). Unique per
     * provider; a replayed webhook with the same id is a no-op (M11).
     */
    externalId: z.string().max(100).nullable(),
    sms: SmsMetaSchema.nullable(),
    /** When scheduled or held: the earliest send time. */
    sendAt: InstantSchema.nullable(),
    occurredAt: InstantSchema,
    /** No hard deletes: a removed bubble shows "Message removed by Ana". */
    removedAt: InstantSchema.nullable(),
    removedByUserId: IdSchema.nullable(),
    removedByName: z.string().max(200).nullable(),
    /** S0 commlog mirror state for manual texts (texting-inbox 4.9). */
    writeBack: WriteBackStateSchema.nullable(),
    ...RowMetaSchema.shape,
  })
  .superRefine((i, ctx) => {
    if (i.actorKind === 'user' && i.actorUserId === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['actorUserId'],
        message: 'user interactions need actorUserId',
      });
    }
    if (i.actorKind === 'patient' && i.direction !== 'inbound') {
      ctx.addIssue({
        code: 'custom',
        path: ['direction'],
        message: 'patient-authored interactions are inbound',
      });
    }
    if (i.channel === 'note' && i.direction !== 'internal') {
      ctx.addIssue({
        code: 'custom',
        path: ['direction'],
        message: 'internal notes are never sent',
      });
    }
    if ((i.channel === 'sms' || i.channel === 'mms') && i.lineId === null) {
      ctx.addIssue({ code: 'custom', path: ['lineId'], message: 'texts record the office line' });
    }
    if (i.removedAt !== null && (i.body !== null || i.removedByUserId === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['removedAt'],
        message: 'a removed interaction has no body and records who removed it',
      });
    }
    const outboundOnly: InteractionStatus[] = [
      'draft',
      'scheduled',
      'held_quiet_hours',
      'held_frequency_cap',
      'held_awaiting_registration',
      'queued',
      'sent',
      'delivered',
      'undelivered',
      'failed',
      'blocked_opted_out',
    ];
    if (i.direction === 'inbound' && i.status !== null && outboundOnly.includes(i.status)) {
      ctx.addIssue({
        code: 'custom',
        path: ['status'],
        message: `inbound interactions cannot be "${i.status}"`,
      });
    }
    if (i.direction === 'outbound' && (i.status === 'received' || i.status === 'read')) {
      ctx.addIssue({
        code: 'custom',
        path: ['status'],
        message: `outbound interactions cannot be "${i.status}"`,
      });
    }
  });
export type Interaction = z.infer<typeof InteractionSchema>;

/* ------------------------------------------------------------------------ */
/* Thread                                                                    */
/* ------------------------------------------------------------------------ */

export const ThreadStatusSchema = z.enum(['open', 'waiting_on_us', 'waiting_on_them', 'closed']);
export type ThreadStatus = z.infer<typeof ThreadStatusSchema>;

/** Inbox splits (M11). */
export const ThreadSplitSchema = z.enum(['patients', 'leads', 'missed_calls', 'reviews', 'staff']);
export type ThreadSplit = z.infer<typeof ThreadSplitSchema>;

/** Status glyph on a thread row (M11). */
export const ThreadGlyphSchema = z.enum([
  'unread',
  'needs_response',
  'replied',
  'error',
  'scheduled',
  'draft',
  'none',
]);
export type ThreadGlyph = z.infer<typeof ThreadGlyphSchema>;

/** One thread per contact per office line (`thread`). */
export const ThreadSchema = z.object({
  id: IdSchema,
  ...LocationOwnedSchema.shape,
  contactId: IdSchema,
  lineId: z.string().min(1).max(64),
  status: ThreadStatusSchema,
  assigneeUserId: IdSchema.nullable(),
  lastInteractionAt: InstantSchema.nullable(),
  unreadCount: z.number().int().nonnegative(),
  ...RowMetaSchema.shape,
});
export type Thread = z.infer<typeof ThreadSchema>;

/**
 * Inbox list row: the thread plus computed fields. `linkedPatientIds` has
 * more than one entry for a shared family number (the UI offers a picker).
 * `preview` is PHI and is null in privacy mode.
 */
export const ThreadListItemSchema = ThreadSchema.extend({
  split: ThreadSplitSchema,
  glyph: ThreadGlyphSchema,
  contactDisplayName: z.string().max(200).nullable(),
  contactPhone: E164Schema.nullable(),
  linkedPatientIds: z.array(IdSchema),
  tags: z.array(z.string().min(1).max(40)),
  snoozedUntil: InstantSchema.nullable(),
  slaDueAt: InstantSchema.nullable(),
  optedOut: z.boolean(),
  preview: z.string().max(200).nullable(),
});
export type ThreadListItem = z.infer<typeof ThreadListItemSchema>;

/** Status a thread should take after a new interaction (actionable logic). */
export function nextThreadStatus(
  current: ThreadStatus,
  interaction: Pick<Interaction, 'direction' | 'actorKind' | 'channel'>,
  opts: { needsResponse: boolean },
): ThreadStatus {
  if (interaction.direction === 'internal') return current;
  if (interaction.direction === 'inbound') return 'waiting_on_us';
  // Outbound automated messages only reopen a thread when they need a response (M11).
  if (interaction.actorKind !== 'user') return opts.needsResponse ? 'waiting_on_them' : current;
  return 'waiting_on_them';
}

/** `GET /v1/inbox/threads`. The inbox is `officeScope: 'location-or-all'`. */
export const InboxQuerySchema = z
  .object({
    view: z.enum(['actionable', 'all', 'failed', 'scheduled', 'after_hours']).default('actionable'),
    split: ThreadSplitSchema.optional(),
    assignee: z.union([z.literal('me'), z.literal('unassigned'), IdSchema]).optional(),
    tag: z.string().min(1).max(40).optional(),
    cursor: CursorSchema.optional(),
    limit: z.number().int().min(1).max(100).default(50),
  })
  .strict();
export type InboxQueryInput = z.input<typeof InboxQuerySchema>;
export type InboxQuery = z.output<typeof InboxQuerySchema>;

/* ------------------------------------------------------------------------ */
/* Sending                                                                   */
/* ------------------------------------------------------------------------ */

/**
 * `POST /v1/inbox/messages`. Outbound stays behind the `sms.enabled` flag
 * until the line's A2P 10DLC campaign is approved (M11).
 */
export const SendMessageRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    /** Existing thread, or `contactId` + `lineId` for a new one. */
    threadId: IdSchema.optional(),
    contactId: IdSchema.optional(),
    lineId: E164Schema.optional(),
    channel: z.enum(['sms', 'mms', 'note']),
    body: z.string().trim().min(1).max(1600),
    purpose: SmsPurposeSchema.default('transactional'),
    attachmentIds: z.array(IdSchema).max(10).default([]),
    /** Schedule for later; quiet hours still apply at send time. */
    sendAt: InstantSchema.optional(),
    /** Patients the reply concerns, for shared family numbers. */
    patientIds: z.array(IdSchema).max(20).optional(),
    /** Close the thread after sending ("Send and Done"). */
    markDone: z.boolean().default(false),
  })
  .strict()
  .superRefine((r, ctx) => {
    const hasThread = r.threadId !== undefined;
    const hasNew = r.contactId !== undefined && r.lineId !== undefined;
    const hasAnyNew = r.contactId !== undefined || r.lineId !== undefined;
    if (hasThread ? hasAnyNew : !hasNew) {
      ctx.addIssue({
        code: 'custom',
        path: ['threadId'],
        message: 'send to a threadId, or to a contactId and lineId, not both',
      });
    }
    if (r.channel !== 'mms' && r.attachmentIds.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['attachmentIds'],
        message: 'attachments need channel "mms"',
      });
    }
    if (r.channel === 'note' && r.sendAt !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['sendAt'],
        message: 'internal notes are not scheduled',
      });
    }
  });
export type SendMessageRequestInput = z.input<typeof SendMessageRequestSchema>;
export type SendMessageRequest = z.output<typeof SendMessageRequestSchema>;

/**
 * A provider-neutral inbound text, produced by the telephony gateway after
 * webhook signature validation (unsigned requests are rejected; M11).
 */
export const InboundMessageSchema = z.object({
  provider: z.enum(['stratus', 'twilio', 'threecx', 'mock']),
  externalId: z.string().min(1).max(100),
  /** The office line that received it (tenant is resolved by this number). */
  to: E164Schema,
  from: E164Schema,
  body: z.string().max(10_000),
  mediaCount: z.number().int().nonnegative(),
  receivedAt: InstantSchema,
});
export type InboundMessage = z.infer<typeof InboundMessageSchema>;

/* ------------------------------------------------------------------------ */
/* Quiet hours and frequency (texting-inbox 4.7)                             */
/* ------------------------------------------------------------------------ */

/** Non-urgent automated sends only between 08:00 and 21:00 recipient local time. */
export const QUIET_HOURS = { allowedFromHour: 8, allowedUntilHour: 21 } as const;

/**
 * True when a recipient-local wall-clock time is inside quiet hours. Invalid
 * input throws instead of returning false, so a NaN hour can never let an
 * automated send through.
 */
export function isQuietHours(localHour: number, localMinute = 0): boolean {
  if (!Number.isInteger(localHour) || localHour < 0 || localHour > 23) {
    throw new RangeError(`local hour must be an integer 0..23, got ${String(localHour)}`);
  }
  if (!Number.isInteger(localMinute) || localMinute < 0 || localMinute > 59) {
    throw new RangeError(`local minute must be an integer 0..59, got ${String(localMinute)}`);
  }
  const minutes = localHour * 60 + localMinute;
  return minutes < QUIET_HOURS.allowedFromHour * 60 || minutes >= QUIET_HOURS.allowedUntilHour * 60;
}

/**
 * Quiet hours for an instant at the recipient's IANA time zone. Uses the tz
 * database through `Intl`, so DST transitions (23- and 25-hour days) and
 * zones without DST (America/Phoenix) resolve correctly. Throws on an invalid
 * instant or zone.
 */
export function isQuietHoursAt(instant: string | Date, timeZone: string): boolean {
  const ms = instant instanceof Date ? instant.getTime() : Date.parse(instant);
  if (!Number.isFinite(ms)) throw new RangeError('invalid instant');
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date(ms));
  } catch {
    throw new RangeError(`invalid time zone ${timeZone}`);
  }
  const hour = Number(parts.find((p) => p.type === 'hour')?.value);
  const minute = Number(parts.find((p) => p.type === 'minute')?.value);
  return isQuietHours(hour, minute);
}

/** Direct replies to an inbound text within this window are exempt from quiet hours. */
export const QUIET_HOURS_REPLY_EXEMPT_MINUTES = 30;

/** Healthcare-exemption cadence for automated transactional texts (FCC 15-72 paragraph 147). */
export const TRANSACTIONAL_FREQUENCY_CAP = { perDay: 1, perWeek: 3 } as const;

export type FrequencyCapDecision =
  { allowed: true } | { allowed: false; reason: 'per_day' | 'per_week'; nextAllowedAt: string };

/**
 * Apply the 1-per-day / 3-per-week cap to an automated transactional send to
 * one number. FCC 15-72 does not define "day" or "week"; Velora uses rolling
 * 24-hour and 7-day windows, which are never looser than calendar windows.
 * `priorSends` are the instants of earlier automated transactional messages
 * to the same number (direct replies are not counted; texting-inbox 4.7).
 */
export function transactionalCapDecision(
  priorSends: readonly string[],
  at: string,
): FrequencyCapDecision {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) throw new RangeError('invalid instant');
  const DAY = 86_400_000;
  const times = priorSends
    .map((x) => Date.parse(x))
    .filter((x) => Number.isFinite(x) && x <= t && x > t - 7 * DAY)
    .sort((a, b) => a - b);
  const inDay = times.filter((x) => x > t - DAY);
  if (inDay.length >= TRANSACTIONAL_FREQUENCY_CAP.perDay) {
    const oldest = inDay[inDay.length - TRANSACTIONAL_FREQUENCY_CAP.perDay] ?? t;
    return {
      allowed: false,
      reason: 'per_day',
      nextAllowedAt: new Date(oldest + DAY).toISOString(),
    };
  }
  if (times.length >= TRANSACTIONAL_FREQUENCY_CAP.perWeek) {
    const oldest = times[times.length - TRANSACTIONAL_FREQUENCY_CAP.perWeek] ?? t;
    return {
      allowed: false,
      reason: 'per_week',
      nextAllowedAt: new Date(oldest + 7 * DAY).toISOString(),
    };
  }
  return { allowed: true };
}

/* ------------------------------------------------------------------------ */
/* Keywords (CTIA 2023; FCC 24-24; confirm-remind 4.3)                       */
/* ------------------------------------------------------------------------ */

/**
 * Full opt-out. Note that CANCEL is a carrier and FCC opt-out word: a patient
 * who replies "Cancel" to a reminder is opted out of SMS, and the inbox also
 * flags the thread so staff follow up about the appointment. Reminder copy
 * must therefore never ask patients to reply CANCEL.
 */
export const STOP_KEYWORDS = [
  'STOP',
  'STOPALL',
  'UNSUBSCRIBE',
  'CANCEL',
  'END',
  'QUIT',
  'REVOKE',
  'OPTOUT',
  'OPT OUT',
  'OPT-OUT',
] as const;
/** Re-subscribe. YES counts only when the number is currently opted out. */
export const START_KEYWORDS = ['START', 'UNSTOP', 'OPTIN', 'OPT IN', 'OPT-IN'] as const;
export const HELP_KEYWORDS = ['HELP', 'INFO'] as const;
/** Marketing-only opt-out (Podium convention, texting-inbox 4.7). */
export const MARKETING_STOP_KEYWORDS = ['REMOVE', 'STOP MARKETING'] as const;
/** Appointment confirmation replies, English and Spanish. */
export const CONFIRM_KEYWORDS = [
  'C',
  'Y',
  'YES',
  'CONFIRM',
  'CONFIRMED',
  'SI',
  'SÍ',
  'CONFIRMO',
] as const;
export const RESCHEDULE_KEYWORDS = ['R', 'RESCHEDULE'] as const;

/**
 * Pictographs that confirm when sent alone: thumbs up (U+1F44D, any skin
 * tone), check mark button (U+2705) and heavy check mark (U+2714). Written as
 * escapes because source files stay emoji-free (BUILD-PLAN 3.1).
 */
const CONFIRM_PICTOGRAPH_RE =
  /^(?:\u{1F44D}[\u{1F3FB}-\u{1F3FF}]?\u{FE0F}?|\u{2705}\u{FE0F}?|\u{2714}\u{FE0F}?)$/u;

/**
 * Natural-language revocation candidates. These do not opt out on their own:
 * they suppress marketing and review immediately and ask staff to confirm a
 * full STOP (texting-inbox 4.7). "please stop by tomorrow" does not match.
 */
const REVOCATION_SUSPECT_RE =
  /\b(?:stop|quit|no more)\s+(?:texting|messaging|sending|contacting|(?:the|these|your)\s+(?:texts?|messages?))\b|\b(?:do\s*n[o'\u2019]?t|never)\s+(?:text|message|contact)\s+me\b|\bno\s+more\s+(?:texts?|messages?)\b|\bwrong\s+number\b|\bunsubscribe\s+me\b|\btake\s+me\s+off\b|\bremove\s+me\b|\bno\s+me\s+(?:manden|mande|env[i\u00ED]en|escriban)\b/i;

/**
 * Spanish sole-word opt-out requests. Not carrier keywords, but a reasonable
 * means of revocation for a Spanish-speaking patient (47 CFR 64.1200(a)(10),
 * FCC 24-24), so they go to staff as revocation suspects.
 */
const SPANISH_REVOCATION_WORDS = ['ALTO', 'PARE', 'PARAR', 'BASTA', 'CANCELAR', 'BAJA'] as const;

/**
 * Courtesy words patients wrap around a keyword ("Stop please", "please
 * stop", "STOP NOW"). Only stripped from the ends, so "please stop by
 * tomorrow" stays a normal message.
 */
const COURTESY_RE =
  /^(?:(?:PLEASE|PLS|PLZ|NOW|THANKS|THANK YOU|THX|TY|OK|OKAY)\s+)+|(?:\s+(?:PLEASE|PLS|PLZ|NOW|THANKS|THANK YOU|THX|TY))+$/g;

function stripCourtesy(k: string): string {
  return k
    .replace(/[\p{P}\p{S}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(COURTESY_RE, '')
    .trim();
}

function isRevocationSuspect(raw: string, k: string): boolean {
  return REVOCATION_SUSPECT_RE.test(raw) || inList(SPANISH_REVOCATION_WORDS, k);
}

export const KEYWORD_KINDS = [
  'stop',
  'start',
  'help',
  'marketing_stop',
  'confirm',
  'reschedule',
  'revocation_suspect',
  'none',
] as const;
export type KeywordKind = (typeof KEYWORD_KINDS)[number];

export interface KeywordClassification {
  kind: KeywordKind;
  /** Canonical keyword matched, upper case; null for `revocation_suspect` and `none`. */
  keyword: string | null;
  /** True for CANCEL: also surface an appointment follow-up to staff. */
  appointmentFollowUp: boolean;
}

/**
 * Upper-cases, trims, collapses spaces and strips surrounding punctuation and
 * symbols, including smart quotes, ellipses, emoji and their variation
 * selectors and joiners (curly-quoted STOP, "Stop" plus an ellipsis and STOP plus a stop-sign
 * normalise to STOP).
 */
export function normalizeKeyword(body: string): string {
  return body
    .normalize('NFC')
    .replace(/\s+/g, ' ')
    .replace(/^[\s\p{P}\p{S}\u{FE0F}\u{200D}]+|[\s\p{P}\p{S}\u{FE0F}\u{200D}]+$/gu, '')
    .toUpperCase();
}

function inList(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

/**
 * Classify an inbound text. Parser order (confirm-remind 4.3): compliance
 * keywords first (exact whole message), then confirmation, then reschedule,
 * then natural-language revocation. Anything else is `none` and goes to the
 * normal inbox (or the intent classifier).
 *
 * A confirmation still only applies to the appointments listed in the most
 * recent pending reminder on that thread; never "the next appointment"
 * blindly. That matching is the inbox service's job.
 */
export function classifyInboundText(
  body: string,
  opts: { optedOut: boolean },
): KeywordClassification {
  const raw = body.trim();
  const k = normalizeKeyword(body);
  const none: KeywordClassification = { kind: 'none', keyword: null, appointmentFollowUp: false };
  if (k === '' && raw === '') return none;
  // "Stop please", "STOP STOP", "stop, thank you": a stop keyword wrapped
  // only in courtesy words or repeated is still a stop.
  const stripped = stripCourtesy(k);
  const words = new Set(stripped.split(' '));
  const bare = words.size === 1 ? ([...words][0] ?? stripped) : stripped;
  const stop = inList(STOP_KEYWORDS, k) ? k : inList(STOP_KEYWORDS, bare) ? bare : null;
  if (stop !== null) {
    return { kind: 'stop', keyword: stop, appointmentFollowUp: stop === 'CANCEL' };
  }
  if (inList(START_KEYWORDS, k) || (opts.optedOut && k === 'YES')) {
    return { kind: 'start', keyword: k, appointmentFollowUp: false };
  }
  if (inList(HELP_KEYWORDS, k)) return { kind: 'help', keyword: k, appointmentFollowUp: false };
  if (inList(MARKETING_STOP_KEYWORDS, k)) {
    return { kind: 'marketing_stop', keyword: k, appointmentFollowUp: false };
  }
  if (opts.optedOut) {
    // An opted-out number cannot confirm by text until it re-subscribes.
    return isRevocationSuspect(raw, k)
      ? { kind: 'revocation_suspect', keyword: null, appointmentFollowUp: false }
      : none;
  }
  if (inList(CONFIRM_KEYWORDS, k) || CONFIRM_PICTOGRAPH_RE.test(raw)) {
    return {
      kind: 'confirm',
      keyword: inList(CONFIRM_KEYWORDS, k) ? k : 'PICTOGRAPH',
      appointmentFollowUp: false,
    };
  }
  if (inList(RESCHEDULE_KEYWORDS, k)) {
    return { kind: 'reschedule', keyword: k, appointmentFollowUp: true };
  }
  if (isRevocationSuspect(raw, k)) {
    return { kind: 'revocation_suspect', keyword: null, appointmentFollowUp: false };
  }
  return none;
}

/* ------------------------------------------------------------------------ */
/* Consent ledger (texting-inbox 4.7)                                        */
/* ------------------------------------------------------------------------ */

export const ConsentSourceSchema = z.enum([
  'keyword',
  'patient_link',
  'staff',
  'intake_form',
  'import',
  'pms_sync',
]);
export type ConsentSource = z.infer<typeof ConsentSourceSchema>;

/**
 * One consent change for one contact point. Append-only. Staff cannot
 * re-opt-in a number that sent STOP: `granted: true` with source `staff` is
 * refused after an opt-out (the patient texts START or uses the preference
 * link).
 */
export const ConsentChangeSchema = z
  .object({
    id: IdSchema,
    practiceId: IdSchema,
    /** Office line the change applies through; null for practice-wide email. */
    locationId: IdSchema.nullable(),
    contactPointId: IdSchema,
    /** `all_sms` is a STOP or START; categories are finer-grained changes. */
    category: z.enum(['all_sms', 'transactional', 'billing', 'marketing', 'review', 'email']),
    granted: z.boolean(),
    source: ConsentSourceSchema,
    lineId: E164Schema.nullable(),
    /** The inbound interaction for keyword changes. */
    interactionId: IdSchema.nullable(),
    actorUserId: IdSchema.nullable(),
    reason: z.string().max(500).nullable(),
    occurredAt: InstantSchema,
  })
  .superRefine((c, ctx) => {
    if (c.source === 'keyword' && c.category === 'email') {
      ctx.addIssue({
        code: 'custom',
        path: ['category'],
        message: 'SMS keywords change SMS consent only',
      });
    }
    if (c.source === 'keyword' && (c.interactionId === null || c.lineId === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['interactionId'],
        message: 'keyword changes record the message and line',
      });
    }
    if (c.source === 'staff' && (c.actorUserId === null || c.reason === null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['reason'],
        message: 'staff changes record who and why',
      });
    }
  });
export type ConsentChange = z.infer<typeof ConsentChangeSchema>;

/** Body of `POST /v1/inbox/messages` 202 response. */
export const SendMessageResultSchema = z.object({
  interaction: InteractionSchema,
  thread: ThreadSchema,
  /** Set when the send was held rather than queued. */
  heldReason: z.enum(['quiet_hours', 'frequency_cap', 'awaiting_registration']).nullable(),
  replayed: z.boolean(),
});
export type SendMessageResult = z.infer<typeof SendMessageResultSchema>;

/* ------------------------------------------------------------------------ */
/* Inbox API views (WP1-INBOX, `/v1/inbox/*`)                                */
/* ------------------------------------------------------------------------ */

/** Response target for a waiting thread, in minutes (texting-inbox 4.4). */
export const INBOX_SLA_MINUTES = 15;

/** Splits the inbox shows. Reviews and staff threads arrive with WP3. */
export const INBOX_SPLITS = ['all', 'patients', 'leads', 'missed_calls'] as const;
export const InboxSplitSchema = z.enum(INBOX_SPLITS);
export type InboxSplit = z.infer<typeof InboxSplitSchema>;

export const InboxViewSchema = z.enum(['actionable', 'all']);
export type InboxView = z.infer<typeof InboxViewSchema>;

/** `GET /v1/inbox/threads` query. */
export const ThreadListQuerySchema = z
  .object({
    view: InboxViewSchema.default('actionable'),
    split: InboxSplitSchema.default('all'),
    assignee: z.union([z.literal('me'), z.literal('unassigned'), IdSchema]).optional(),
    q: z.string().trim().max(80).optional(),
    /** Keep this thread in the list even when it no longer matches (just marked done). */
    keep: IdSchema.optional(),
    /**
     * Narrow to unanswered threads (Home "Unanswered texts"), or to those
     * whose wait began with a reply to the automatic missed-call text (Home
     * "Missed-call texts with a reply nobody answered").
     */
    filter: z.enum(['unanswered', 'missed_call_replies']).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
  })
  .strict();
export type ThreadListQuery = z.output<typeof ThreadListQuerySchema>;

export const AssigneeSchema = z.object({ userId: IdSchema, name: z.string() });
export type Assignee = z.infer<typeof AssigneeSchema>;

/**
 * What the patient's unanswered text is about, read from its words and
 * keyword (the list shows these as chips so staff triage without opening).
 */
export const THREAD_INTENTS = ['reschedule', 'billing', 'forms', 'arrived'] as const;
export const ThreadIntentSchema = z.enum(THREAD_INTENTS);
export type ThreadIntent = z.infer<typeof ThreadIntentSchema>;

export const ThreadRowSchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  lineId: z.string(),
  status: ThreadStatusSchema,
  split: z.enum(['patients', 'leads', 'missed_calls']),
  /** Also has a recent unanswered missed call (shows in the Missed split too). */
  missedCall: z.boolean(),
  glyph: ThreadGlyphSchema,
  actionable: z.boolean(),
  assignee: AssigneeSchema.nullable(),
  /** Patient name, or the contact's name, or null (show the number). PHI. */
  displayName: z.string().nullable(),
  phone: z.string().nullable(),
  patientIds: z.array(IdSchema),
  tags: z.array(z.string()),
  snoozedUntil: InstantSchema.nullable(),
  /** First inbound message not answered yet; drives the SLA chip. */
  waitingSince: InstantSchema.nullable(),
  lastAt: InstantSchema.nullable(),
  /** Last message preview. PHI. */
  preview: z.string().nullable(),
  previewActor: z.enum(['patient', 'staff', 'automation', 'note', 'event']).nullable(),
  previewActorName: z.string().nullable(),
  unreadCount: z.number().int().nonnegative(),
  optedOut: z.boolean(),
  /** Intents of the latest unanswered patient text; empty once answered. */
  intents: z.array(ThreadIntentSchema).default([]),
  /**
   * The patient texted and no staff member has answered: no staff text or
   * staff note since, and the thread was not marked done. Automatic messages
   * (missed-call text, reminders, after-hours reply) never count as an
   * answer. Null when answered, done or snoozed.
   */
  unanswered: z
    .object({
      /** The first unanswered patient text. */
      since: InstantSchema,
      /** The wait began with a reply to the automatic missed-call text. */
      afterMissedCallText: z.boolean(),
      /** Minutes of the wait the office was open (office hours, closed holidays); null without hours. */
      openMinutes: z.number().int().nonnegative().nullable(),
    })
    .nullable()
    .default(null),
});
export type ThreadRow = z.infer<typeof ThreadRowSchema>;

export const ThreadListResponseSchema = z.object({
  items: z.array(ThreadRowSchema),
  /** Actionable threads per split in the current office scope. */
  counts: z.object({
    all: z.number().int().nonnegative(),
    patients: z.number().int().nonnegative(),
    leads: z.number().int().nonnegative(),
    missed_calls: z.number().int().nonnegative(),
  }),
  waiting: z.number().int().nonnegative(),
  overSla: z.number().int().nonnegative(),
  slaMinutes: z.number().int().positive(),
  /** Unanswered threads in scope (see ThreadRow.unanswered), and those after a missed-call text. */
  unanswered: z.number().int().nonnegative().default(0),
  missedCallReplies: z.number().int().nonnegative().default(0),
});
export type ThreadListResponse = z.infer<typeof ThreadListResponseSchema>;

/** Timeline event kinds written by the inbox (keyword handling, confirms, calls). */
export const INBOX_EVENT_KINDS = [
  'confirmed',
  'confirm_review',
  'nothing_to_confirm',
  'opted_out',
  'opted_in',
  'marketing_opt_out',
  'help',
  'possible_revocation',
  'follow_up',
  'missed_call',
  'call',
  'voicemail',
] as const;
export const InboxEventKindSchema = z.enum(INBOX_EVENT_KINDS);
export type InboxEventKind = z.infer<typeof InboxEventKindSchema>;

export const ConfirmWriteBackSchema = z.enum([
  'applied',
  'pending',
  'pms_unavailable',
  'writes_paused',
]);
export type ConfirmWriteBack = z.infer<typeof ConfirmWriteBackSchema>;

export const MessageViewSchema = z.object({
  id: IdSchema,
  kind: z.enum(['message', 'event']),
  channel: InteractionChannelSchema,
  direction: DirectionSchema,
  actorKind: InteractionActorKindSchema,
  actorName: z.string().nullable(),
  status: z.string().nullable(),
  /** PHI. Null when removed. */
  body: z.string().nullable(),
  /** Inbound compliance or confirm keyword, normalised (STOP, Y, ...). */
  keyword: z.string().nullable(),
  purpose: SmsPurposeSchema.nullable(),
  occurredAt: InstantSchema,
  sendAt: InstantSchema.nullable(),
  removed: z.object({ byName: z.string(), at: InstantSchema }).nullable(),
  sms: z
    .object({
      encoding: z.enum(['gsm7', 'ucs2']).nullable(),
      segments: z.number().int().nullable(),
      /** True: passed every gate but the office channel is not live; never reached a carrier. */
      simulated: z.boolean(),
      failure: z.string().nullable(),
      message: z.string().nullable(),
      /** Provider error code verbatim (Twilio 30034, 30007, 21610, ...). */
      errorCode: z.string().nullable().optional(),
    })
    .nullable(),
  event: z
    .object({
      kind: InboxEventKindSchema,
      text: z.string(),
      writeBack: ConfirmWriteBackSchema.nullable(),
    })
    .nullable(),
  /** The caller may remove this bubble (their own outbound text; a placeholder stays). */
  removable: z.boolean(),
  /**
   * Who or what sent an outbound message, in words staff read on the bubble:
   * "Automatic missed-call text", "Reminder", "Statement", "AI receptionist",
   * "Staff: Jane Doe". Null for inbound messages and events.
   */
  senderLabel: z.string().nullable().default(null),
  /** Shown from another thread of the same patient at this office (e.g. a missed-call text sent on the call line). */
  fromOtherThread: z.boolean().default(false),
});
export type MessageView = z.infer<typeof MessageViewSchema>;

export const ConfirmStateSchema = z.enum([
  'unconfirmed',
  'confirmed',
  /** Confirmed in Velora; appointment.confirm queued for the PMS. */
  'pending',
  /** Confirmed in Velora; the PMS cannot take it (Open Dental on PulseSync v1). */
  'pms_unavailable',
  /** Confirmed in Velora; writes to this office's PMS are paused. */
  'writes_paused',
  'left_message',
  'unreachable',
  'sent_text',
  'sent_email',
]);
export type ConfirmState = z.infer<typeof ConfirmStateSchema>;

export const ContextAppointmentSchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  patientId: IdSchema,
  patientName: z.string(),
  startsAt: InstantSchema,
  lengthMin: z.number().int(),
  typeName: z.string().nullable(),
  colorToken: z.string().nullable(),
  providerName: z.string().nullable(),
  codes: z.array(z.string()),
  confirmState: ConfirmStateSchema,
  /** Why Velora could not write the confirmation to the PMS, when it could not. */
  writeBackNote: z.string().nullable(),
});
export type ContextAppointment = z.infer<typeof ContextAppointmentSchema>;

export const ContextPatientSchema = z.object({
  id: IdSchema,
  name: z.string(),
  firstName: z.string(),
  preferredName: z.string().nullable(),
  birthdate: z.string().nullable(),
  status: z.string(),
  chartNumber: z.string().nullable(),
  /** The thread's contact is this patient (not a household member). */
  primary: z.boolean(),
  relationship: z.string().nullable(),
  alerts: z.array(z.string()),
  /** Ledger balance in cents; null without money.view. */
  balanceCents: z.number().int().nullable(),
  recallDue: z.string().nullable(),
  /** Planned treatment total in cents; null without money.view. */
  openTreatmentCents: z.number().int().nullable(),
  /** Primary dental plan on file (carrier, plan type, network), or null. */
  insurance: z
    .object({
      carrier: z.string(),
      planType: z.string(),
      network: z.enum(['in', 'out', 'unknown']),
    })
    .nullable()
    .default(null),
});
export type ContextPatient = z.infer<typeof ContextPatientSchema>;

export const ConsentChannelStateSchema = z.enum(['opted_in', 'opted_out', 'unknown']);
export type ConsentChannelState = z.infer<typeof ConsentChannelStateSchema>;
export const ConsentLogEntrySchema = z.object({
  at: InstantSchema,
  kind: z.string(),
  text: z.string(),
  source: z.string(),
});
export const ThreadConsentSchema = z.object({
  transactional: ConsentChannelStateSchema,
  marketing: ConsentChannelStateSchema,
  /** The carrier blocks this number until it texts START. */
  carrierBlocked: z.boolean(),
  /** Where the current transactional state came from (keyword_stop, pms_import, ...). */
  source: z.string().nullable(),
  changedAt: InstantSchema.nullable(),
  /** A longer message looked like "stop texting me": staff should confirm. */
  revocationSuspected: z.boolean(),
  log: z.array(ConsentLogEntrySchema),
});
export type ThreadConsent = z.infer<typeof ThreadConsentSchema>;

/**
 * A2P 10DLC campaign status. Read-only in Velora: set by the provider or the
 * registration flow, never by a staff dropdown (C-07). `unknown` = not read
 * from the provider (self-attested values were reset to it, V20 migration).
 */
export const A2pStatusViewSchema = z.enum([
  'unregistered',
  'pending',
  'verified',
  'rejected',
  'unknown',
]);

/** Where outbound texts for a line end up right now (`canText` in @velora/domain/consent). */
export const TextingModeSchema = z.enum(['live', 'simulated', 'held']);
export const TextingBlockSchema = z.enum([
  'no_number',
  'sms_disabled',
  'a2p_not_verified',
  'not_connected',
  'live_sends_off',
]);
export const LineTextingSchema = z.object({
  mode: TextingModeSchema,
  block: TextingBlockSchema.nullable(),
  message: z.string(),
});
export type LineTexting = z.infer<typeof LineTextingSchema>;

export const InboxLineSchema = z.object({
  locationId: IdSchema,
  lineId: z.string(),
  e164: z.string().nullable(),
  label: z.string(),
  officeName: z.string(),
  smsEnabled: z.boolean(),
  a2pStatus: A2pStatusViewSchema,
  carrierHandlesReplies: z.boolean(),
  /** Outbound texts can leave Velora from this line right now. */
  canSend: z.boolean(),
  /** Plain-English reason when they cannot. */
  sendBlockedReason: z.string().nullable(),
  /** VELORA_SMS_LIVE is on for this server (a provider must still be bound to deliver). */
  liveSends: z.boolean(),
  /** The one texting decision for this line (held in production until a provider is bound). */
  texting: LineTextingSchema.default({
    mode: 'held',
    block: 'not_connected',
    message: 'Texting is not connected; nothing is delivered.',
  }),
});
export type InboxLine = z.infer<typeof InboxLineSchema>;

export const ConfirmCapabilitySchema = z.object({
  /** velora: Velora owns appointments here; pms: appointment.confirm is queued. */
  mode: z.enum(['velora', 'pms', 'pms_unavailable', 'writes_paused']),
  reason: z.string().nullable(),
  /** `confirmWritePolicy(office).writes`: a confirm queues a PMS command (delayed commit in the UI). */
  writesToPms: z.boolean().default(false),
  /** Persistent row label when the PMS is not written ("Kept in Velora only"). */
  label: z.string().nullable().default(null),
  /** PMS display name ("Open Dental"), null for a Velora-native office. */
  pmsName: z.string().nullable().default(null),
});
export type ConfirmCapability = z.infer<typeof ConfirmCapabilitySchema>;

/** `GET /v1/inbox/threads/:id`. Opening it marks the thread read and audits a PHI read. */
export const ThreadDetailSchema = z.object({
  thread: ThreadRowSchema,
  messages: z.array(MessageViewSchema),
  contact: z.object({
    id: IdSchema,
    displayName: z.string().nullable(),
    phone: z.string().nullable(),
    /**
     * Caller ID name (CNAM lookup) for a number on no patient and with no
     * contact name; caller ID only, never a verified person. Null otherwise.
     */
    callerIdName: z.string().nullable().default(null),
  }),
  patients: z.array(ContextPatientSchema),
  appointments: z.array(ContextAppointmentSchema),
  consent: ThreadConsentSchema,
  line: InboxLineSchema,
  confirm: ConfirmCapabilitySchema,
  /** Merge values for composer placeholders (null when there is no value). PHI. */
  mergeValues: z.record(z.string(), z.string().nullable()),
});
export type ThreadDetail = z.infer<typeof ThreadDetailSchema>;

/** `POST /v1/inbox/threads/:id/messages`. */
export const ComposeRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    channel: z.enum(['sms', 'note']),
    /** Final text: placeholders already resolved by the composer. */
    body: z.string().trim().min(1).max(1600),
    purpose: SmsPurposeSchema.default('transactional'),
    /** Staff acknowledged a consent warning (opted-out number, unknown consent). */
    acknowledgeWarning: z.boolean().default(false),
    markDone: z.boolean().default(false),
  })
  .strict();
export type ComposeRequestInput = z.input<typeof ComposeRequestSchema>;
export type ComposeRequest = z.output<typeof ComposeRequestSchema>;

/**
 * `POST /v1/inbox/threads/:id/ai-draft`: an AI-written reply for staff to
 * read, edit and send themselves. Nothing is sent: the draft only fills the
 * composer, and the send then passes every usual gate (consent, A2P, the
 * automations master switch for patient texts). Grounded in the thread and
 * the patient's upcoming visits; the model runs only on the practice's
 * approved AI lane (on-prem, or a BAA vendor).
 */
export const AiDraftRequestSchema = z
  .object({
    /** Optional staff hint ("offer Thursday morning", "ask them to call"). */
    hint: z.string().trim().max(200).optional(),
  })
  .strict();
export type AiDraftRequest = z.input<typeof AiDraftRequestSchema>;

export const AiDraftResultSchema = z.object({
  /** PHI. The suggested reply, never sent by Velora on its own. */
  text: z.string(),
  /** What the draft was grounded in, in plain words ("Next visit Tue Oct 27, 9:00 AM"). */
  basis: z.array(z.string()),
  /** The model that wrote the draft, as the AI gateway reports it. */
  model: z.string().nullable(),
  generatedAt: InstantSchema,
});
export type AiDraftResult = z.infer<typeof AiDraftResultSchema>;

export const HeldReasonSchema = z.enum([
  /** No live provider is bound (or live sends are off): nothing is delivered. */
  'not_connected',
  'sms_disabled',
  'a2p_not_verified',
  'opted_out',
  'gateway_refused',
  /** Settings > Automations & notifications is off (master switch or category). */
  'automations_off',
]);
export type HeldReason = z.infer<typeof HeldReasonSchema>;

export const ComposeResultSchema = z.object({
  message: MessageViewSchema,
  thread: ThreadRowSchema,
  /** Why the text did not leave Velora (null when the gateway accepted it). */
  held: z.object({ reason: HeldReasonSchema, message: z.string() }).nullable(),
  replayed: z.boolean(),
});
export type ComposeResult = z.infer<typeof ComposeResultSchema>;

/**
 * `POST /v1/inbox/threads`: open the conversation with a patient or a phone
 * number, creating it when none exists yet (staff start a text to someone
 * who never texted in; deep links from the call pop, missed calls and the
 * patient file land on the right thread). Idempotent: the same patient or
 * number at the same office always returns the same thread. A new thread
 * holds no messages and stays out of the list until something is sent.
 */
export const OpenThreadRequestSchema = z
  .object({
    patientId: IdSchema.optional(),
    /** Any US format; normalised to E.164. */
    phone: z.string().trim().min(7).max(32).optional(),
    /** Office whose line the text goes out on; required in All offices when it cannot be inferred. */
    locationId: IdSchema.optional(),
  })
  .strict()
  .refine((v) => v.patientId !== undefined || v.phone !== undefined, {
    message: 'Give a patient or a phone number.',
  });
export type OpenThreadRequest = z.infer<typeof OpenThreadRequestSchema>;

export const OpenThreadResultSchema = z.object({
  thread: ThreadRowSchema,
  created: z.boolean(),
});
export type OpenThreadResult = z.infer<typeof OpenThreadResultSchema>;

/** `POST /v1/inbox/threads/:id/assign`. `userId: null` unassigns; omitted assigns the caller. */
export const AssignRequestSchema = z.object({ userId: IdSchema.nullable().optional() }).strict();
export type AssignRequest = z.infer<typeof AssignRequestSchema>;

/** `POST /v1/inbox/threads/:id/status`. */
export const ThreadStatusRequestSchema = z
  .object({
    action: z.enum(['done', 'reopen', 'unread', 'snooze']),
    until: InstantSchema.optional(),
  })
  .strict()
  .refine((r) => r.action !== 'snooze' || r.until !== undefined, {
    message: 'snooze needs until',
    path: ['until'],
  });
export type ThreadStatusRequest = z.infer<typeof ThreadStatusRequestSchema>;

/** `POST /v1/inbox/threads/:id/confirm`: staff confirm the listed appointments. */
export const ConfirmRequestSchema = z
  .object({
    idempotencyKey: IdempotencyKeySchema,
    appointmentIds: z.array(IdSchema).min(1).max(20),
  })
  .strict();
export type ConfirmRequest = z.infer<typeof ConfirmRequestSchema>;

export const ConfirmResultSchema = z.object({
  confirmed: z.array(
    z.object({
      appointmentId: IdSchema,
      confirmState: ConfirmStateSchema,
      note: z.string().nullable(),
    }),
  ),
  replayed: z.boolean(),
});
export type ConfirmResult = z.infer<typeof ConfirmResultSchema>;

/** `POST /v1/inbox/threads/:id/consent`: staff apply a full STOP after a written request. */
export const ConsentActionRequestSchema = z
  .object({ action: z.literal('apply_stop'), reason: z.string().trim().min(3).max(500) })
  .strict();
export type ConsentActionRequest = z.infer<typeof ConsentActionRequestSchema>;

export const StaffMemberSchema = z.object({
  userId: IdSchema,
  name: z.string(),
});
export type StaffMember = z.infer<typeof StaffMemberSchema>;

export const InboxTemplateSchema = z.object({
  id: IdSchema,
  locationId: IdSchema.nullable(),
  name: z.string(),
  purpose: SmsPurposeSchema,
  body: z.string(),
  placeholders: z.array(z.string()),
  archived: z.boolean(),
  updatedAt: InstantSchema,
});
export type InboxTemplate = z.infer<typeof InboxTemplateSchema>;

export const TemplateUpsertSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    purpose: SmsPurposeSchema,
    body: z.string().trim().min(1).max(1600),
    locationId: IdSchema.nullable().default(null),
  })
  .strict();
export type TemplateUpsertInput = z.input<typeof TemplateUpsertSchema>;
export type TemplateUpsert = z.output<typeof TemplateUpsertSchema>;

export const TemplateIssueViewSchema = z.object({
  code: z.string(),
  severity: z.enum(['error', 'warning']),
  message: z.string(),
  token: z.string().optional(),
});
export type TemplateIssueView = z.infer<typeof TemplateIssueViewSchema>;

export const TemplateSaveResultSchema = z.object({
  template: InboxTemplateSchema,
  issues: z.array(TemplateIssueViewSchema),
});

/**
 * `PUT /v1/inbox/lines/:locationId` (settings.manage). The A2P status is not
 * settable here (audit C-07): it comes from the office's A2P registration
 * record (`/v1/messaging/a2p/:locationId`). `a2pStatus` is still parsed so
 * the route can answer 403 with that reason instead of a bare 400.
 */
export const LineUpdateSchema = z
  .object({
    smsEnabled: z.boolean().optional(),
    a2pStatus: A2pStatusViewSchema.optional(),
    carrierHandlesReplies: z.boolean().optional(),
  })
  .strict();
export type LineUpdate = z.infer<typeof LineUpdateSchema>;

/**
 * `GET /v1/inbox/status`: the page-level texting state for the offices in
 * scope (C-03 banner) and, for settings.manage, which inbound webhook secrets
 * are configured (C-04). Presence flags only, never a secret value.
 */
/** `GET /v1/inbox/unread`: the top-bar Messages badge (no PHI, counts only). */
export const InboxUnreadSchema = z.object({
  /** Open patient conversations with unread inbound texts, in the offices the caller sees. */
  threads: z.number().int().nonnegative(),
  /** Unread inbound texts across those conversations. */
  messages: z.number().int().nonnegative(),
});
export type InboxUnread = z.infer<typeof InboxUnreadSchema>;

export const TextingStatusSchema = z.object({
  environment: z.enum(['production', 'development', 'test']),
  providerBound: z.boolean(),
  liveSends: z.boolean(),
  banner: z
    .object({
      tone: z.enum(['warning', 'info']),
      title: z.string(),
      detail: z.string().nullable(),
    })
    .nullable(),
  lines: z.array(
    z.object({
      locationId: IdSchema,
      officeName: z.string(),
      texting: LineTextingSchema,
    }),
  ),
  /** Null without settings.manage. */
  webhooks: z
    .object({
      /** Twilio inbound/status webhooks: an auth token is configured. */
      twilioConfigured: z.boolean(),
      /** Velora-signed relay webhook (Stratus relay): at least one secret is configured. */
      relayConfigured: z.boolean(),
      /** Inbound texts can arrive at all (either one is configured). */
      inboundPossible: z.boolean(),
    })
    .nullable(),
});
export type TextingStatus = z.infer<typeof TextingStatusSchema>;

/**
 * Placeholder spellings the seed and older templates use, mapped to the
 * `@velora/domain/consent` merge-field registry. Both spellings resolve.
 */
export const PLACEHOLDER_ALIASES: Readonly<Record<string, string>> = {
  office_name: 'location_name',
  office_phone: 'location_phone',
  office_address: 'location_address',
  balance: 'balance_due',
  provider: 'provider_display',
};

/**
 * `GET|PUT /v1/inbox/after-hours/:locationId`: the office's after-hours
 * auto-reply (engagement gaps, task 3). Off by default. When on, a patient
 * who texts while the office is closed gets this message once per thread per
 * `cooldownHours`, never after STOP / START / HELP / REMOVE, never to an
 * opted-out number and never outside 8 AM to 8 PM office time.
 */
export const AfterHoursReplySchema = z.object({
  locationId: IdSchema,
  officeName: z.string(),
  enabled: z.boolean(),
  body: z.string(),
  cooldownHours: z.number().int(),
  /** False when the office has no hours set: Velora cannot tell when it is closed. */
  hoursKnown: z.boolean(),
  /** Whether the office is closed right now (office hours and closed holidays). */
  closedNow: z.boolean(),
});
export type AfterHoursReply = z.infer<typeof AfterHoursReplySchema>;

export const AfterHoursReplyUpdateSchema = z
  .object({
    enabled: z.boolean(),
    body: z.string().trim().min(1).max(320),
    cooldownHours: z.number().int().min(1).max(72),
  })
  .strict();
export type AfterHoursReplyUpdate = z.infer<typeof AfterHoursReplyUpdateSchema>;

/* ------------------------------------------------------------------------ */
/* Everything: one list for every kind of item waiting on the office         */
/* ------------------------------------------------------------------------ */

/**
 * Owner demo 2026-11-02 (Dr. John): staff log into three or four portals
 * (reviews, appointment requests, texts, reminders); everything should land
 * in one place, assignable to one person, showing who replied.
 * `GET /v1/inbox/items` lists every kind in one list; each row opens the page
 * that works it. Read-only aggregation plus assignment: nothing is sent.
 */
export const INBOX_ITEM_KINDS = [
  'text',
  'portal_message',
  'missed_call',
  'voicemail',
  'web_lead',
  'appointment_request',
  'portal_request',
  'review',
] as const;
export const InboxItemKindSchema = z.enum(INBOX_ITEM_KINDS);
export type InboxItemKind = z.infer<typeof InboxItemKindSchema>;

export const INBOX_ITEM_LABEL: Readonly<Record<InboxItemKind, string>> = Object.freeze({
  text: 'Text',
  portal_message: 'Portal message',
  missed_call: 'Missed call',
  voicemail: 'Voicemail',
  web_lead: 'Web lead',
  appointment_request: 'Appointment request',
  portal_request: 'Portal request',
  review: 'Review',
});

export const InboxItemSchema = z.object({
  /** `<kind>:<id>`. */
  key: z.string().max(100),
  kind: InboxItemKindSchema,
  /** The source row: thread, call, lead, booking request, portal request or review. */
  id: IdSchema,
  locationId: IdSchema,
  /** When it came in (the first unanswered text, the call, the request, the review). */
  at: InstantSchema,
  /** Who: the patient, the caller, the reviewer. PHI. */
  who: z.string().max(200),
  /** What: a preview, the request, the review text. PHI; null when there is none. */
  what: z.string().max(300).nullable(),
  /** A short fact: "Web chat", "2 stars, Google", "Reschedule", "3 calls". */
  detail: z.string().max(120).nullable(),
  urgent: z.boolean(),
  assignee: AssigneeSchema.nullable(),
  /** Who answered it and how ("Replied by text", "Called back", "Approved"). Null while waiting. */
  handled: z
    .object({ by: z.string().max(200).nullable(), at: InstantSchema, how: z.string().max(80) })
    .nullable(),
  /** Where it is worked (a Velora route and its search params). */
  open: z.object({ to: z.string().max(80), search: z.record(z.string(), z.string()) }),
});
export type InboxItem = z.infer<typeof InboxItemSchema>;

export const InboxItemsQuerySchema = z
  .object({
    /** `waiting` (default) or `handled` (answered in the last 2 days). */
    status: z.enum(['waiting', 'handled']).default('waiting'),
    assignee: z.union([z.literal('me'), z.literal('unassigned'), IdSchema]).optional(),
    kind: InboxItemKindSchema.optional(),
  })
  .strict();
export type InboxItemsQuery = z.output<typeof InboxItemsQuerySchema>;

export const InboxItemsResponseSchema = z.object({
  items: z.array(InboxItemSchema),
  /** Waiting items per kind in scope (before the assignee and kind filters). */
  counts: z.record(InboxItemKindSchema, z.number().int().nonnegative()),
  /** Kinds this user cannot see at these offices, or whose source failed to load. */
  unavailable: z.array(InboxItemKindSchema),
});
export type InboxItemsResponse = z.infer<typeof InboxItemsResponseSchema>;

/** `POST /v1/inbox/items/assign`: `userId: null` unassigns; omitted assigns the caller. */
export const InboxItemAssignSchema = z
  .object({ kind: InboxItemKindSchema, id: IdSchema, userId: IdSchema.nullable().optional() })
  .strict();
export type InboxItemAssign = z.infer<typeof InboxItemAssignSchema>;
